import React, {ButtonHTMLAttributes, ReactElement} from "react";
import {Guild, GuildsApi} from "@devour/client";
import {useGetGuildProgress} from "@/hooks/useGetGuildProgress";
import {useDispatch, useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import {addError, decrementLoading, incrementLoading} from "@/redux/meta/metaActions";
import FrameButton from "@/components/buttons/FrameButton";
import classNames from "classnames";
import {DateTime} from "luxon";
import {MdCheck} from "react-icons/md";
import {isDesktop, isTablet} from "react-device-detect";
import useGetGuildOwnerships from "@/hooks/useGetGuildOwnerships";

interface Props {
    guild: Guild;
    isOnModal?: boolean;
}

function GuildMintBadgeButton(props: Props): ReactElement {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { data: guildProgress, refetch: refetchGuildProgress } = useGetGuildProgress(props.guild?.id, fullToken);
    const {refetch: refetchGuildOwnerships} = useGetGuildOwnerships(fullToken, false);


    const renderClaimStr = () => {
        if (guildProgress?.hasUserMintedBadge) {
            const completedDate = DateTime
                .fromMillis(guildProgress?.mintBadgeDate)
                .toLocaleString({month: "short", day: "numeric"});

            if (!props.isOnModal && (isDesktop || isTablet)) {
                return <>Claimed <MdCheck/></>;
            }

            return <>Claimed on {completedDate}<MdCheck/></>;
        } if (!guildProgress?.isGuildQualifierQuestsCompleted) {
            return "Not Yet Claimed";
        }
        return "Claim";
    };

    async function mintGuildBadge(): Promise<void> {
        if (guildProgress?.hasUserMintedBadge) {
            return;
        }
        dispatch(incrementLoading());
        try {
            await new GuildsApi(getConfig()).mintGuildBadge({
                id: props.guild?.id,
            });
            await Promise.all([
                refetchGuildProgress(),
                refetchGuildOwnerships(),
            ]);
        } catch (e) {
            dispatch(await addError(e));
        } finally {
            dispatch(decrementLoading());
        }

    }

    return (
        <div className="guild-mint-badge">
            <FrameButton
                <ButtonHTMLAttributes<HTMLButtonElement>>
                color={guildProgress?.hasUserMintedBadge ? undefined : "devourplay-gradient"}
                size="narrow"
                onClick={mintGuildBadge}
                className={classNames("guild-mint-badge-btn", {
                    "claimed": guildProgress?.hasUserMintedBadge || !guildProgress?.isGuildQualifierQuestsCompleted,
                })}
                forwardProps={{
                    disabled: !guildProgress?.isGuildQualifierQuestsCompleted,
                }}
            >
                {renderClaimStr()}
            </FrameButton>
        </div>
    );
}

export default GuildMintBadgeButton;
